import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@material-ui/core';
import AvTimerIcon from '@material-ui/icons/AvTimer';
import { useFormik } from 'formik';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { useState, useRef, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import Modal from 'react-modal';
import { logTimeApi } from 'src/api/logTimeApi';
import * as Yup from 'yup';
import { Calendar } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { addDays } from 'date-fns';

Modal.setAppElement('#root'); // Set the root element for accessibility

interface LogTimeModalProps {
  isOpen: boolean;
  onClose: (state) => void;
  billingAccounts: Array<any>;
  caseId?: string;
  caseKey?: string;
  caseSummary?: string;
  loggedTimeEditData?: any;
  convertSecondsToTimeString?: any;
  sprintOptions?: any;
  caseSprint?: any;
  fixVersion?: any;
  fixVersionOptions?: any;
  reloadData?: () => void;
  caseBillingAccount?: any;
}

const LogTimeModal = ({
  isOpen,
  onClose,
  billingAccounts,
  caseId,
  caseKey,
  caseSummary,
  loggedTimeEditData,
  convertSecondsToTimeString,
  sprintOptions,
  caseSprint,
  fixVersion,
  fixVersionOptions,
  reloadData,
  caseBillingAccount,
}: LogTimeModalProps) => {
  const [loading, setloading] = useState(false);
  const modalStyle = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      transform: 'translate(-50%, -50%)',
      position: 'absolute',
      width: '40%',
      borderRadius: '10px',
    },
  };

  const convertToSeconds = (timeString) => {
    const timeUnits = timeString?.match(/\d+\s*[hm]/g);
    let totalSeconds = 0;

    timeUnits?.forEach((unit) => {
      const number = parseInt(unit, 10);
      const timeUnit = unit?.replace(/\d+\s*/g, ''); // Remove digits and spaces to get the unit

      if (timeUnit === 'h') {
        totalSeconds += number * 3600; // 1 hour = 3600 seconds
      } else if (timeUnit === 'm') {
        totalSeconds += number * 60; // 1 minute = 60 seconds
      }
    });

    return totalSeconds || 0;
  };

  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
    key: 'selection',
  });

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const calendarRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsCalendarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      loggedHoursInSeconds: !isEmpty(loggedTimeEditData)
        ? convertSecondsToTimeString(loggedTimeEditData?.loggedHoursInSeconds)
        : null,
      // date: !isEmpty(loggedTimeEditData)
      //   ? moment(loggedTimeEditData?.date).format('YYYY-MM-DD')
      //   : moment().format('YYYY-MM-DD'),
      description: !isEmpty(loggedTimeEditData)
        ? loggedTimeEditData?.description
        : '',
      billableAccount: !isEmpty(loggedTimeEditData)
        ? loggedTimeEditData?.billableAccount
        : billingAccounts.find(
            (account) => account._id === caseBillingAccount?.id
          ) || null,
      sprint: !isEmpty(loggedTimeEditData)
        ? sprintOptions.find(
            (sprint) => sprint.id === loggedTimeEditData?.sprint?.id
          )
        : sprintOptions.find((sprint) => sprint.id === caseSprint?.id) || null,
      date: !isEmpty(loggedTimeEditData)
        ? moment(loggedTimeEditData?.date).format('YYYY-MM-DD')
        : (() => {
            const selectedSprint = sprintOptions.find(
              (sprint) => sprint.id === caseSprint?.id
            );
            if (selectedSprint) {
              const endDate = new Date(selectedSprint.endDate);
              const startDate = new Date(selectedSprint.startDate);
              const isSprintPast = moment(endDate).isBefore(moment(), 'day');
              const isSprintFuture = moment(startDate).isAfter(moment(), 'day');

              if (isSprintPast) {
                return moment(endDate).format('YYYY-MM-DD');
              }
              if (isSprintFuture) {
                return moment(startDate).format('YYYY-MM-DD');
              }
            }
            return moment().format('YYYY-MM-DD');
          })(),
      fixVersion: (() => {
        if (!isEmpty(loggedTimeEditData)) {
          return loggedTimeEditData?.releaseVersion || null;
        }
        return fixVersion?.length ? fixVersion[0] : null;
      })(),
    },
    onSubmit: async (values) => {
      setloading(true);
      const hoursInSeconds = convertToSeconds(values.loggedHoursInSeconds);
      const updatedValues = {
        ...values,
        loggedHoursInSeconds: hoursInSeconds,
        date: `${moment(values?.date).format('YYYY-MM-DD')}T00:00:00`,
        billableAccount: {
          id: values.billableAccount?._id,
          name: values.billableAccount?.name,
        },
        releaseVersion: values.fixVersion,
        sprint: {
          ...values.sprint,
          name: values.sprint?.name?.replace(' (Current Sprint)', '').trim(),
        },
      };

      try {
        let response: any = {};
        if (isEmpty(loggedTimeEditData)) {
          response = await logTimeApi?.logTime(caseId, updatedValues);
        } else {
          response = await logTimeApi?.editLogTime(
            caseId,
            loggedTimeEditData?.id,
            updatedValues
          );
        }
        setloading(false);
        toast.success('Time logged successfully');
        if (reloadData !== undefined) {
          reloadData();
        }
        formik.resetForm();
        onClose(false);
      } catch (error) {
        if (error?.response?.status !== 401) {
          setloading(false);
          toast.error(error?.response?.data?.message);
        }
      }
    },
    validationSchema: Yup.object().shape({
      description: Yup.string().trim().required('Description is required'),
      loggedHoursInSeconds: Yup.string()
        .nullable()
        .required('Please enter the time duration')
        .matches(
          /^(?:\d+\s*[hm]\s*)+$/,
          'Allocated hours must be in the format "number h" or "number m" or a combination like "2h 20m"'
        )
        .test(
          'non-zero-duration',
          'Duration must be greater than 0',
          (value) => {
            const totalSeconds = convertToSeconds(value);
            return totalSeconds > 0;
          }
        ),
      billableAccount: Yup.object()
        .required('Billable Account is required')
        .nullable(),
      sprint: Yup.object().required('Sprint is required').nullable(),
      fixVersion: Yup.object().required('Fix Version is required').nullable(),
    }),
  });

  const handleSprintChange = (event, value) => {
    formik.setFieldValue('sprint', value);
    if (value) {
      const startDate = new Date(value.startDate);
      const endDate = new Date(value.endDate);

      // Check if sprint is in the past (end date is before today)
      const isSprintPast = moment(endDate).isBefore(moment(), 'day');
      const isSprintFuture = moment(startDate).isAfter(moment(), 'day');

      let defaultDate;
      if (isSprintPast) {
        defaultDate = moment(endDate).format('YYYY-MM-DD');
      } else if (isSprintFuture) {
        defaultDate = moment(startDate).format('YYYY-MM-DD');
      } else {
        defaultDate = moment().format('YYYY-MM-DD');
      }

      formik.setFieldValue('date', defaultDate);
    }
  };

  // eslint-disable-next-line
  const sortedSprintOptions = useMemo(() => {
    return sprintOptions.map((sprint, index) => ({
      ...sprint,
      name: index === 0 ? `${sprint.name} (Current Sprint)` : sprint.name, // usman ki farmaish
    }));
  }, [sprintOptions]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => onClose(false)}
      style={modalStyle}>
      <Box
        sx={{
          padding: 3,
        }}>
        <Stack spacing={2}>
          <Typography
            sx={{ display: 'flex', fontSize: 16 }}
            variant="h4"
            fontWeight={500}>
            <AvTimerIcon sx={{ marginRight: '10px' }} />
            Log time
          </Typography>
          {/* Issue Section */}
          <div>
            <Typography
              sx={{ display: 'flex', fontSize: 16, marginBottom: '10px' }}
              variant="h5"
              fontWeight={500}>
              Issue
            </Typography>
            <TextField
              value={`${caseKey} - ${caseSummary}`}
              variant="outlined"
              disabled
              fullWidth
            />
          </div>
          {/* Sprint and Fix Version Dropdowns */}
          <Grid display="flex" justifyContent="space-between">
            <Grid item md={5}>
              <Typography
                sx={{ display: 'flex', fontSize: 16, marginBottom: '10px' }}
                variant="h5"
                fontWeight={500}>
                Sprint
              </Typography>
              <Autocomplete
                options={sortedSprintOptions}
                value={
                  sortedSprintOptions.find(
                    (option) => option.id === formik.values.sprint?.id
                  ) || null
                }
                onChange={handleSprintChange}

                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderOption={(props, option) => (
                  <li {...props}>
                    {option.id === sortedSprintOptions[0]?.id ? (
                      <strong>{option.name}</strong>
                    ) : (
                      option.name
                    )}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    name="sprint"
                    placeholder="Select Sprint"
                    error={Boolean(
                      formik.touched.sprint && formik.errors.sprint
                    )}
                    helperText={formik.touched.sprint && formik.errors.sprint}
                  />
                )}
              />
              {/* <Autocomplete
                options={sprintOptions}
                value={formik.values.sprint}
                onChange={handleSprintChange}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    name="sprint"
                    placeholder="Select Sprint"
                    error={Boolean(
                      formik.touched.sprint && formik.errors.sprint
                    )}
                    helperText={formik.touched.sprint && formik.errors.sprint}
                  />
                )}
              /> */}
            </Grid>
            <Grid item md={5}>
              <Typography
                sx={{ display: 'flex', fontSize: 16, marginBottom: '10px' }}
                variant="h5"
                fontWeight={500}>
                Fix Version
              </Typography>
              <Autocomplete
                options={fixVersionOptions}
                value={formik.values.fixVersion}
                onChange={(event, value) =>
                  formik.setFieldValue('fixVersion', value)
                }
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    name="fixVersion"
                    placeholder="Select Fix Version"
                    error={Boolean(
                      formik.touched.fixVersion && formik.errors.fixVersion
                    )}
                    helperText={
                      formik.touched.fixVersion && formik.errors.fixVersion
                    }
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid display="flex" justifyContent="space-between">
            <Grid item md={5}>
              <Typography
                sx={{ display: 'flex', fontSize: 16, marginBottom: '10px' }}
                variant="h5"
                fontWeight={500}>
                Date
              </Typography>
              <div ref={calendarRef} style={{ position: 'relative' }}>
                <TextField
                  value={moment(formik.values.date).format('DD MMM YYYY')}
                  onClick={() => setIsCalendarOpen(true)}
                  variant="outlined"
                  fullWidth
                  sx={{ cursor: 'pointer' }}
                />
                {isCalendarOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      zIndex: 1000,
                      backgroundColor: 'white',
                      boxShadow: '0px 4px 10px rgba(0,0,0,0.1)',
                      marginTop: '8px',
                    }}>
                    <Calendar
                      date={new Date(formik.values.date)}
                      onChange={(date) => {
                        formik.setFieldValue(
                          'date',
                          moment(date).format('YYYY-MM-DD')
                        );
                        setIsCalendarOpen(false);
                      }}
                      minDate={
                        formik.values.sprint?.startDate
                          ? new Date(formik.values.sprint.startDate)
                          : undefined
                      }
                      maxDate={
                        formik.values.sprint?.endDate
                          ? new Date(formik.values.sprint.endDate)
                          : undefined
                      }
                      color="#2196f3"
                    />
                  </div>
                )}
              </div>
            </Grid>
            <Grid item md={5}>
              <Typography
                sx={{ display: 'flex', fontSize: 16, marginBottom: '10px' }}
                variant="h5"
                fontWeight={500}>
                Duration
              </Typography>
              <TextField
                value={formik.values.loggedHoursInSeconds}
                onChange={formik.handleChange}
                variant="outlined"
                name="loggedHoursInSeconds"
                type="text"
                fullWidth
                placeholder="0h"
                error={Boolean(
                  formik?.touched?.loggedHoursInSeconds &&
                    formik?.errors?.loggedHoursInSeconds
                )}
                helperText={
                  formik?.touched?.loggedHoursInSeconds &&
                  formik?.errors?.loggedHoursInSeconds
                }
              />
            </Grid>
          </Grid>
          <div>
            <Typography
              sx={{ display: 'flex', fontSize: 16, marginBottom: '10px' }}
              variant="h5"
              fontWeight={500}>
              Description
            </Typography>
            <TextField
              value={formik.values.description}
              onChange={formik.handleChange}
              variant="outlined"
              name="description"
              rows={4}
              multiline
              fullWidth
              placeholder="Description"
              error={Boolean(
                formik.touched.description && formik.errors.description
              )}
              helperText={
                formik.touched.description && formik.errors.description
              }
            />
          </div>
          <div>
            <Typography
              sx={{ display: 'flex', fontSize: 16, marginBottom: '10px' }}
              variant="h5"
              fontWeight={500}>
              Account
            </Typography>
            <Autocomplete
              options={billingAccounts}
              value={formik.values.billableAccount}
              onChange={(event: any, value: any) => {
                formik.setFieldValue('billableAccount', value);
              }}
              disabled
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  name="billableAccount"
                  placeholder="Account"
                  InputProps={{
                    ...params.InputProps,
                    readOnly: true,
                  }}
                  error={Boolean(
                    formik.touched.billableAccount &&
                      formik.errors.billableAccount
                  )}
                  helperText={
                    formik.touched.billableAccount &&
                    formik.errors.billableAccount
                  }
                />
              )}
            />
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}>
            <Button
              variant="outlined"
              color="primary"
              sx={{ marginLeft: 'auto', height: '38px' }}
              onClick={() => onClose(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              sx={{ height: '38px', marginLeft: '12px' }}
              onClick={() => !loading && formik.handleSubmit()}>
              {loading ? (
                <CircularProgress
                  size="20px"
                  sx={{
                    color: '#FFFFFF',
                  }}
                />
              ) : (
                'Log Time'
              )}
            </Button>
          </div>
        </Stack>
      </Box>
    </Modal>
  );
};

export default LogTimeModal;
